import * as React from "react";
import { useUploadMedia } from "@/hooks/use-media";
import { MediaPicker } from "./media-picker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, FolderOpen, Plus, Edit2, Trash2, Play } from "lucide-react";

interface FeaturedImageSectionProps {
  logo: string;
  pictures: string[];
  videos: Array<{
    title: string;
    url: string;
    thumbnail?: string;
    duration?: string;
    views?: string;
    channel?: string;
  }>;
  onLogoChange: (logo: string) => void;
  onPicturesChange: (pictures: string[]) => void;
  onVideosChange: (videos: any[]) => void;
}

export function FeaturedImageSection({
  logo,
  pictures,
  videos,
  onLogoChange,
  onPicturesChange,
  onVideosChange,
}: FeaturedImageSectionProps) {
  const [logoPickerOpen, setLogoPickerOpen] = React.useState(false);
  const [picturesPickerOpen, setPicturesPickerOpen] = React.useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = React.useState(false);
  const [isUploadingPictures, setIsUploadingPictures] = React.useState(false);
  const [youtubeUrl, setYoutubeUrl] = React.useState("");
  const [isAddingVideo, setIsAddingVideo] = React.useState(false);
  const [videoError, setVideoError] = React.useState<string | null>(null);
  const [editingVideoIndex, setEditingVideoIndex] = React.useState<number | null>(null);
  const [previewVideo, setPreviewVideo] = React.useState<{ url: string; title: string } | null>(null);
  const uploadMedia = useUploadMedia();

  const handleLogoUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          setIsUploadingLogo(true);
          const result = await uploadMedia.mutateAsync({
            file,
            folder: "agents/logos",
          });
          if (result?.media?.url) {
            onLogoChange(result.media.url);
          }
        } catch (error) {
          console.error("Logo upload error:", error);
        } finally {
          setIsUploadingLogo(false);
        }
      }
    };
    input.click();
  };

  const handlePicturesUpload = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        try {
          setIsUploadingPictures(true);
          const uploadPromises = files.map((file) =>
            uploadMedia.mutateAsync({
              file,
              folder: "agents/pictures",
            })
          );
          const results = await Promise.all(uploadPromises);
          const uploadedUrls = results
            .map((result) => result?.media?.url)
            .filter(Boolean) as string[];
          onPicturesChange([...pictures, ...uploadedUrls]);
        } catch (error) {
          console.error("Pictures upload error:", error);
        } finally {
          setIsUploadingPictures(false);
        }
      }
    };
    input.click();
  };

  const handleRemovePicture = (index: number) => {
    const newPictures = pictures.filter((_, i) => i !== index);
    onPicturesChange(newPictures);
  };

  const extractYouTubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const handleAddYouTubeVideo = async () => {
    if (!youtubeUrl.trim()) return;

    try {
      setIsAddingVideo(true);
      setVideoError(null);
      
      // Extract video ID from YouTube URL
      const videoId = extractYouTubeVideoId(youtubeUrl.trim());
      if (!videoId) {
        setVideoError("URL YouTube không hợp lệ. Vui lòng nhập URL đúng định dạng (vd: https://www.youtube.com/watch?v=...)");
        setIsAddingVideo(false);
        return;
      }

      // Fetch metadata from YouTube API
      try {
        // Try to use API server URL, fallback to web app URL
        const apiUrl = import.meta.env.VITE_API_URL || window.location.origin.replace(':5173', ':3000');
        const webAppUrl = window.location.origin.replace(':5173', ':3000');
        const metadataUrl = `${apiUrl}/api/youtube/metadata`;
        
        let response = await fetch(`${metadataUrl}?url=${encodeURIComponent(youtubeUrl.trim())}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        // If API server doesn't have this endpoint, try web app
        if (!response.ok && apiUrl !== webAppUrl) {
          response = await fetch(`${webAppUrl}/api/youtube/metadata?url=${encodeURIComponent(youtubeUrl.trim())}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        }
        
        if (response.ok) {
          const metadata = await response.json() as {
            title: string;
            channel: string;
            thumbnail: string;
            thumbnailHighRes?: string;
            videoId: string;
          };
          
          // Create video object with real metadata
          const newVideo = {
            title: metadata.title,
            url: youtubeUrl.trim(),
            thumbnail: metadata.thumbnail,
            channel: metadata.channel || undefined,
          };

          onVideosChange([...videos, newVideo]);
          setYoutubeUrl("");
          setVideoError(null);
          return;
        }
      } catch (fetchError) {
        console.warn("Failed to fetch YouTube metadata, using fallback:", fetchError);
      }

      // Fallback: Create video object with basic info using reliable hqdefault
      const newVideo = {
        title: `YouTube Video ${videos.length + 1}`,
        url: youtubeUrl.trim(),
        thumbnail: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
      };

      onVideosChange([...videos, newVideo]);
      setYoutubeUrl("");
      setVideoError(null);
    } catch (err) {
      console.error("Add YouTube video error:", err);
      setVideoError(err instanceof Error ? err.message : "Có lỗi khi thêm video. Vui lòng thử lại.");
    } finally {
      setIsAddingVideo(false);
    }
  };

  const handleRemoveVideo = (index: number) => {
    const newVideos = videos.filter((_, i) => i !== index);
    onVideosChange(newVideos);
  };

  const handleEditVideo = (index: number) => {
    const video = videos[index];
    setYoutubeUrl(video.url);
    setEditingVideoIndex(index);
  };

  const handleUpdateVideo = async () => {
    if (editingVideoIndex === null) return;

    try {
      setIsAddingVideo(true);
      setVideoError(null);
      
      const videoId = extractYouTubeVideoId(youtubeUrl.trim());
      if (!videoId) {
        setVideoError("URL YouTube không hợp lệ.");
        setIsAddingVideo(false);
        return;
      }

      try {
        // Try to use API server URL, fallback to web app URL
        const apiUrl = import.meta.env.VITE_API_URL || window.location.origin.replace(':5173', ':3000');
        const webAppUrl = window.location.origin.replace(':5173', ':3000');
        const metadataUrl = `${apiUrl}/api/youtube/metadata`;
        
        let response = await fetch(`${metadataUrl}?url=${encodeURIComponent(youtubeUrl.trim())}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        // If API server doesn't have this endpoint, try web app
        if (!response.ok && apiUrl !== webAppUrl) {
          response = await fetch(`${webAppUrl}/api/youtube/metadata?url=${encodeURIComponent(youtubeUrl.trim())}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
        }
        
        if (response.ok) {
          const metadata = await response.json() as {
            title: string;
            channel: string;
            thumbnail: string;
            thumbnailHighRes?: string;
            videoId: string;
          };
          
          const updatedVideos = [...videos];
          updatedVideos[editingVideoIndex] = {
            title: metadata.title,
            url: youtubeUrl.trim(),
            thumbnail: metadata.thumbnail,
            channel: metadata.channel || undefined,
          };
          onVideosChange(updatedVideos);
          setYoutubeUrl("");
          setEditingVideoIndex(null);
          setVideoError(null);
          return;
        }
      } catch (fetchError) {
        console.warn("Failed to fetch YouTube metadata:", fetchError);
      }

      // Fallback
      const updatedVideos = [...videos];
      updatedVideos[editingVideoIndex] = {
        ...updatedVideos[editingVideoIndex],
        url: youtubeUrl.trim(),
        thumbnail: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`
      };
      onVideosChange(updatedVideos);
      setYoutubeUrl("");
      setEditingVideoIndex(null);
      setVideoError(null);
    } catch (err) {
      console.error("Update video error:", err);
      setVideoError(err instanceof Error ? err.message : "Có lỗi khi cập nhật video.");
    } finally {
      setIsAddingVideo(false);
    }
  };

  const handleCancelEdit = () => {
    setYoutubeUrl("");
    setEditingVideoIndex(null);
    setVideoError(null);
  };

  const getYouTubeEmbedUrl = (url: string): string => {
    // Extract video ID from various YouTube URL formats
    let videoId: string | null = null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        videoId = match[1];
        break;
      }
    }
    
    if (!videoId) {
      return url;
    }
    
    // Build embed URL
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const handlePreviewVideo = (video: { url: string; title: string }) => {
    setPreviewVideo(video);
  };

  const handleClosePreview = () => {
    setPreviewVideo(null);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Featured Image</h3>
      </div>
      <div className="p-6 space-y-4">
        {/* Logo Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Logo
          </label>
          {logo ? (
            <div className="relative">
              <img
                src={logo}
                alt="Agent logo"
                className="w-full h-32 object-contain rounded-lg border border-gray-200 bg-gray-50"
              />
              <button
                onClick={() => onLogoChange("")}
                className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                title="Xóa logo"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                onClick={handleLogoUpload}
                disabled={isUploadingLogo}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploadingLogo ? (
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                    <p className="text-sm font-medium text-gray-600">
                      Đang tải lên...
                    </p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-4" />
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      Tải lên logo
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF tối đa 50MB
                    </p>
                  </>
                )}
              </button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setLogoPickerOpen(true)}
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Chọn từ Media Library
              </Button>
            </div>
          )}
        </div>

        {/* Additional Images Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional Images
          </label>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handlePicturesUpload}
                disabled={isUploadingPictures}
              >
                {isUploadingPictures ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                    Đang tải lên...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Tải lên hình ảnh
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setPicturesPickerOpen(true)}
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Chọn từ Library
              </Button>
            </div>

            {/* Pictures Grid */}
            {pictures.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                {pictures.map((picture, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={picture}
                      alt={`Additional image ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      onClick={() => handleRemovePicture(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                      title="Xóa ảnh"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Videos Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Videos
          </label>
          <div className="space-y-3">
            {/* Add Video Input */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="Nhập URL YouTube (vd: https://www.youtube.com/watch?v=...)"
                  value={youtubeUrl}
                  onChange={(e) => {
                    setYoutubeUrl(e.target.value);
                    if (videoError) setVideoError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && youtubeUrl.trim() && !isAddingVideo) {
                      if (editingVideoIndex !== null) {
                        handleUpdateVideo();
                      } else {
                        handleAddYouTubeVideo();
                      }
                    }
                  }}
                  className={videoError ? "border-red-500" : ""}
                />
                {editingVideoIndex !== null ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleUpdateVideo}
                      disabled={isAddingVideo || !youtubeUrl.trim()}
                    >
                      {isAddingVideo ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                          Đang cập nhật...
                        </>
                      ) : (
                        <>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Cập nhật
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleCancelEdit}
                      disabled={isAddingVideo}
                    >
                      Hủy
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    onClick={handleAddYouTubeVideo}
                    disabled={isAddingVideo || !youtubeUrl.trim()}
                  >
                    {isAddingVideo ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                        Đang thêm...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Thêm video
                      </>
                    )}
                  </Button>
                )}
              </div>
              {videoError && (
                <p className="text-sm text-red-500">{videoError}</p>
              )}
            </div>

            {/* Videos List */}
            {videos.length > 0 && (
              <div className="space-y-3">
                {videos.map((video, index) => (
                  <div key={index} className="relative group border border-gray-200 rounded-lg p-3 hover:border-primary transition-colors cursor-pointer" onClick={() => handlePreviewVideo(video)}>
                    <div className="flex gap-3">
                      {/* Thumbnail */}
                      <div className="relative flex-shrink-0">
                        {video.thumbnail ? (
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-32 h-20 object-cover rounded border border-gray-200"
                          />
                        ) : (
                          <div className="w-32 h-20 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                            <Play className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 transition-colors rounded">
                          <Play className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                      
                      {/* Video Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {video.title || `Video ${index + 1}`}
                        </h4>
                        {video.channel && (
                          <p className="text-xs text-gray-500 mt-1 truncate">
                            {video.channel}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {video.url}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditVideo(index)}
                          className="p-1.5 text-gray-500 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                          title="Chỉnh sửa"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRemoveVideo(index)}
                          className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Xóa"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Preview Modal */}
      {previewVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={handleClosePreview}>
          <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{previewVideo.title}</h3>
              <button
                onClick={handleClosePreview}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Video Player */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={getYouTubeEmbedUrl(previewVideo.url)}
                className="absolute top-0 left-0 w-full h-full rounded-b-lg"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={previewVideo.title}
              />
            </div>
          </div>
        </div>
      )}

      {/* Media Picker Modals */}
      <MediaPicker
        isOpen={logoPickerOpen}
        onClose={() => setLogoPickerOpen(false)}
        onSelect={(url) => {
          onLogoChange(url);
          setLogoPickerOpen(false);
        }}
        fileType="image"
      />

      <MediaPicker
        isOpen={picturesPickerOpen}
        onClose={() => setPicturesPickerOpen(false)}
        onSelectMultiple={(urls) => {
          onPicturesChange([...pictures, ...urls]);
          setPicturesPickerOpen(false);
        }}
        fileType="image"
        multiple={true}
      />
    </div>
  );
}